import { type EnvironmentId, WS_METHODS } from "@afar-research/contracts";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as SubscriptionRef from "effect/SubscriptionRef";

import {
  type GitHubRoutingIdentity,
  type GitHubRoutingPermission,
  gitHubRoutingPermissionFor,
  isGitHubRoutingAllowed,
  isSameGitHubIdentity,
  type StoredGitHubRoutingPermission,
} from "../connection/githubRouting.ts";
import { EnvironmentNotRegisteredError, EnvironmentRegistry } from "../connection/registry.ts";
import { EnvironmentSupervisor } from "../connection/supervisor.ts";
import { request } from "../rpc/client.ts";

/** A machine that answered slower than this is not worth holding a pull request view open for. */
const IDENTITY_TIMEOUT = "2 seconds";

/**
 * Reads one Environment's GitHub identity from its own source-control discovery, or null.
 *
 * Null means "do not route here". An Environment that cannot say who it is signed in as is not
 * one to borrow GitHub access from, so an unknown identity fails closed rather than being treated
 * as a match.
 */
export const readGitHubRoutingIdentity = Effect.fn("PullRequestRouting.identity")(function* (
  registry: EnvironmentRegistry["Service"],
  environmentId: EnvironmentId,
) {
  const discovery = yield* registry
    .run(environmentId, request(WS_METHODS.serverDiscoverSourceControl, {}))
    .pipe(Effect.timeout(IDENTITY_TIMEOUT), Effect.option, Effect.map(Option.getOrNull));
  const github = discovery?.sourceControlProviders.find((provider) => provider.kind === "github");
  if (github === undefined || github.auth.status !== "authenticated") return null;
  const host = Option.getOrNull(github.auth.host);
  const account = Option.getOrNull(github.auth.account);
  return host === null || account === null
    ? null
    : ({ host, account } satisfies GitHubRoutingIdentity);
});

export interface PullRequestRoutingCandidate {
  readonly environmentId: EnvironmentId;
  readonly permission: GitHubRoutingPermission;
}

/**
 * The Environments a pull request request for `origin` may be retried on, nearest first.
 *
 * Only the permission check happens here. Identity is verified per candidate at dispatch time,
 * because a machine can be signed out of GitHub between the moment this list is built and the
 * moment the request would run.
 */
export const routingCandidates = Effect.fn("PullRequestRouting.candidates")(function* (input: {
  readonly registry: EnvironmentRegistry["Service"];
  readonly origin: EnvironmentId;
  readonly permissions: ReadonlyArray<StoredGitHubRoutingPermission>;
  readonly write: boolean;
}) {
  const entries = yield* SubscriptionRef.get(input.registry.entries);
  const originEntry = entries.get(input.origin);
  if (originEntry === undefined) return [];
  const originPermission = gitHubRoutingPermissionFor(originEntry, input.permissions);
  const candidates: Array<PullRequestRoutingCandidate> = [];
  for (const [environmentId, entry] of entries) {
    if (environmentId === input.origin) continue;
    const permission = gitHubRoutingPermissionFor(entry, input.permissions);
    if (
      !isGitHubRoutingAllowed({
        origin: originPermission,
        destination: permission,
        write: input.write,
      })
    ) {
      continue;
    }
    candidates.push({ environmentId, permission });
  }
  return candidates;
});

/**
 * Runs a pull request read on its own Environment, falling back to a machine the user has let it
 * borrow GitHub access from.
 *
 * A candidate is used only once it proves it is signed in to the same GitHub account, checked at
 * dispatch rather than when the candidate list was built, because a machine can sign out in
 * between.
 */
export const routePullRequestRead = <A, E, R>(input: {
  readonly registry: EnvironmentRegistry["Service"];
  readonly origin: EnvironmentId;
  readonly permissions: ReadonlyArray<StoredGitHubRoutingPermission>;
  readonly read: Effect.Effect<A, E, R>;
  /**
   * Where an Environment's GitHub identity comes from. Defaults to asking that Environment's own
   * source-control discovery; it is a parameter because what counts as "the same account" is a
   * policy, and because it is read lazily, one candidate at a time, rather than up front.
   */
  readonly identity?: (
    registry: EnvironmentRegistry["Service"],
    environmentId: EnvironmentId,
  ) => Effect.Effect<GitHubRoutingIdentity | null>;
}): Effect.Effect<A, E | EnvironmentNotRegisteredError, Exclude<R, EnvironmentSupervisor>> => {
  /**
   * Tries each borrowed machine in turn, and re-raises the owning machine's failure when none
   * works. A borrowed machine's error would name a machine the user never asked about, and its
   * absence is not evidence about the one they did.
   */
  const readIdentity = input.identity ?? readGitHubRoutingIdentity;
  const borrow = (originalFailure: E | EnvironmentNotRegisteredError) =>
    Effect.gen(function* () {
      const candidates = yield* routingCandidates({
        registry: input.registry,
        origin: input.origin,
        permissions: input.permissions,
        write: false,
      });
      if (candidates.length === 0) return yield* Effect.fail(originalFailure);
      const originIdentity = yield* readIdentity(input.registry, input.origin);
      if (originIdentity === null) return yield* Effect.fail(originalFailure);
      for (const candidate of candidates) {
        const identity = yield* readIdentity(input.registry, candidate.environmentId);
        if (!isSameGitHubIdentity(originIdentity, identity)) continue;
        const routed = yield* input.registry
          .run(candidate.environmentId, input.read)
          .pipe(Effect.option);
        if (Option.isSome(routed)) return routed.value;
      }
      return yield* Effect.fail(originalFailure);
    });

  // The origin is tried first and its answer preferred: routing exists to rescue a read the
  // owning machine cannot serve, not to move pull request traffic off it.
  return input.registry.run(input.origin, input.read).pipe(Effect.catch(borrow));
};
